import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Permissions } from '../../utils/permissions';
import AppShell from '../shell/AppShell';
import { visibleNavigation } from '../shell/navigation';

import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import IssuedUsers from './pages/IssuedUsers';
import IssuedUserLog from './pages/IssuedUserLog';
import Payouts from './pages/Payouts';
import WebHookLogs from './pages/WebHookLogs';
import BusinessInfo from './pages/BusinessInfo';
import Team from './pages/Team';
import Developer from './pages/Developer';
import SubMerchants from './pages/SubMerchants';
import SubMerchantDetail from './pages/SubMerchantDetail';
import SubMerchantDashboard from './pages/SubMerchantDashboard';
import SubMerchantTransactions from './pages/SubMerchantTransactions';
import SubMerchantLedger from './pages/SubMerchantLedger';
import SubMerchantWithdrawals from './pages/SubMerchantWithdrawals';
import SubMerchantBankAccount from './pages/SubMerchantBankAccount';
import EnterpriseDashboard from './pages/EnterpriseDashboard';
import EnterpriseTransactions from './pages/EnterpriseTransactions';
import EnterpriseLedger from './pages/EnterpriseLedger';
import EnterprisePayouts from './pages/EnterprisePayouts';
import EnterpriseBankAccount from './pages/EnterpriseBankAccount';
import EnterpriseMerchants from './pages/EnterpriseMerchants';
import EnterpriseMerchantDetail from './pages/EnterpriseMerchantDetail';
import AgentDashboard from './pages/AgentDashboard';
import AgentTransactions from './pages/AgentTransactions';
import AgentCommissions from './pages/AgentCommissions';

import { PageLoader } from '../ui';
import NoAccess from './partials/NoAccess';

const useVisibleNavigation = () => {
  const { isAgent, isEnterprise, isSubMerchant, hasPermission } = useAuth();
  return visibleNavigation({ isAgent, isEnterprise, isSubMerchant, hasPermission });
};

const Landing = ({ items }) => (items[0] ? <Navigate to={items[0].to} replace /> : <NoAccess />);

const LandingRedirect = () => {
  const items = useVisibleNavigation();
  return <Landing items={items} />;
};

const RequireNav = ({ id, children }) => {
  const items = useVisibleNavigation();

  if (items.some((item) => item.id === id)) return children;
  return <Landing items={items} />;
};

// business-info has no sidebar entry — it is reached from the header name dropdown — so it is
// gated on its permission and stays available in partner and agent mode. Sub-merchant and enterprise mode
// are shut out because their users hold `partner.settings.password.update`, which the wildcard matches.
const RequireBusinessInfo = ({ children }) => {
  const { hasPermission, isSubMerchant, isEnterprise } = useAuth();
  const items = useVisibleNavigation();

  if (!isSubMerchant && !isEnterprise && hasPermission(Permissions.settingsAny)) return children;
  return <Landing items={items} />;
};

const Layout = () => {
  const { authData, permissionsLoading, permissionsError, permissions, detailsLoaded, refreshPermissions } =
    useAuth();

  if (!authData.token) return <Navigate to="/" replace />;
  if (permissionsLoading) return <PageLoader />;
  // Without /details the session's mode is unknown, and guessing partner mode would strand a
  // sub-merchant user on screens that all answer 403.
  if (permissionsError && (permissions.length === 0 || !detailsLoaded)) {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route
            path="*"
            element={
              <NoAccess
                title="Something went wrong"
                message="We couldn't load your permissions. Check your connection and try again."
                onRetry={refreshPermissions}
              />
            }
          />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<LandingRedirect />} />
        <Route
          path="dashboard"
          element={
            <RequireNav id="dashboard">
              <Dashboard />
            </RequireNav>
          }
        />
        <Route
          path="transactions"
          element={
            <RequireNav id="transactions">
              <Transactions />
            </RequireNav>
          }
        />
        <Route
          path="issued-users"
          element={
            <RequireNav id="issued-users">
              <IssuedUsers />
            </RequireNav>
          }
        />
        <Route
          path="issued-users/issued-user-log"
          element={
            <RequireNav id="issued-users">
              <IssuedUserLog />
            </RequireNav>
          }
        />
        <Route
          path="payouts"
          element={
            <RequireNav id="payouts">
              <Payouts />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchants"
          element={
            <RequireNav id="sub-merchants">
              <SubMerchants />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchants/:subMerchantId"
          element={
            <RequireNav id="sub-merchants">
              <SubMerchantDetail />
            </RequireNav>
          }
        />
        <Route
          path="webhook-logs"
          element={
            <RequireNav id="webhook-logs">
              <WebHookLogs />
            </RequireNav>
          }
        />
        <Route
          path="business-info"
          element={
            <RequireBusinessInfo>
              <BusinessInfo />
            </RequireBusinessInfo>
          }
        />
        <Route
          path="team"
          element={
            <RequireNav id="team">
              <Team />
            </RequireNav>
          }
        />
        <Route
          path="developer"
          element={
            <RequireNav id="developer">
              <Developer />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchant-dashboard"
          element={
            <RequireNav id="sub-merchant-dashboard">
              <SubMerchantDashboard />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchant-transactions"
          element={
            <RequireNav id="sub-merchant-transactions">
              <SubMerchantTransactions />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchant-ledger"
          element={
            <RequireNav id="sub-merchant-ledger">
              <SubMerchantLedger />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchant-withdrawals"
          element={
            <RequireNav id="sub-merchant-withdrawals">
              <SubMerchantWithdrawals />
            </RequireNav>
          }
        />
        <Route
          path="sub-merchant-bank-account"
          element={
            <RequireNav id="sub-merchant-bank-account">
              <SubMerchantBankAccount />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-dashboard"
          element={
            <RequireNav id="enterprise-dashboard">
              <EnterpriseDashboard />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-transactions"
          element={
            <RequireNav id="enterprise-transactions">
              <EnterpriseTransactions />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-ledger"
          element={
            <RequireNav id="enterprise-ledger">
              <EnterpriseLedger />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-payouts"
          element={
            <RequireNav id="enterprise-payouts">
              <EnterprisePayouts />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-bank-account"
          element={
            <RequireNav id="enterprise-bank-account">
              <EnterpriseBankAccount />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-merchants"
          element={
            <RequireNav id="enterprise-merchants">
              <EnterpriseMerchants />
            </RequireNav>
          }
        />
        <Route
          path="enterprise-merchants/:merchantId"
          element={
            <RequireNav id="enterprise-merchants">
              <EnterpriseMerchantDetail />
            </RequireNav>
          }
        />
        <Route
          path="agent-dashboard"
          element={
            <RequireNav id="agent-dashboard">
              <AgentDashboard />
            </RequireNav>
          }
        />
        <Route
          path="agent-transactions"
          element={
            <RequireNav id="agent-transactions">
              <AgentTransactions />
            </RequireNav>
          }
        />
        <Route
          path="agent-commissions"
          element={
            <RequireNav id="agent-commissions">
              <AgentCommissions />
            </RequireNav>
          }
        />
      </Route>
    </Routes>
  );
};

export default Layout;
